import { defineComponent as _defineComponent } from 'vue'
import { toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, createTextVNode as _createTextVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, createVNode as _createVNode, renderList as _renderList, Fragment as _Fragment, unref as _unref, createBlock as _createBlock } from "vue"

const _hoisted_1 = { class: "font-size-18-600" }
const _hoisted_2 = {
  key: 0,
  class: "font-size-10-400"
}
const _hoisted_3 = {
  key: 0,
  class: "font-size-10-400"
}
const _hoisted_4 = { class: "pt-12" }

import {computed, onMounted, shallowRef, inject} from 'vue';
import {useRouter} from 'vue-router';
import {useCartStore} from '@/api/store/cart';
import {cartService} from '@/api/services/cart';
import {useSettingsStore} from '@/api/store/settings';

import QuantityEditor from '@/components/QuantityEditor.vue';
import ShopCartProductDetails from "@/views/ShopComponents/ShopCart/ShopCartProductDetails.vue";
import ButtonFixed from "@/components/custom/ButtonFixed.vue";
import ShopCartServicePackageDetails from "@/views/ShopComponents/ShopCart/ShopCartServicePackageDetails.vue";

import debounce from 'lodash/debounce';


export default /*@__PURE__*/_defineComponent({
  __name: 'ShopCartView',
  setup(__props) {

const settingsStore = useSettingsStore();
const cartStore = useCartStore();
const router = useRouter();

const snackBarPlugin = inject('snackbar');

const loadingOnRemove = shallowRef();
const loadingOnChangeQuantity = shallowRef();

const getCarts = async () => {
  try {
    const response = await cartService.index();

    if (Array.isArray(response.data)) {
      const newItems: any[] = [];

      response.data.forEach((product: any) => {
        product.items.forEach((item: any) => {
          newItems.push({
            ...item,
            ...product,
            item_id: item.id,
          });
        });
      });

      cartStore.items = newItems;

      // Now check if any `shipping_method` item exists without `Product` items
      removeShippingIfNoProducts(newItems);
    }
  } catch (error) {
    console.log(error);
  }
};

const removeShippingIfNoProducts = (items: any[]) => {
  // Check for the shipping_method item
  const shippingMethodItem = items.find(item => item.product?.type === 'shipping_method');

  // Check if there are no Product items in the cart
  const hasProducts = items.some(item => item.product.type === 'clinic_product');

  if (shippingMethodItem && !hasProducts) {
    // Remove the shipping address automatically
    removeFromCart(shippingMethodItem.cart_id, shippingMethodItem.item_id, true);
  }
};

const removeFromCart = (cartId: any, itemId: any, shippingMethodWithoutProduct = false) => {
  loadingOnRemove.value = true;
  cartService.removeItem(cartId, itemId).then((response: any) => {
    getCarts();
    loadingOnRemove.value = false;

    if (!shippingMethodWithoutProduct) {
      snackBarPlugin.successSnackbar('Item removed from cart');
    }
  }).catch((error: any) => {
    console.log(error);
  });
};

const changeQuantity = (item: any, quantity: number) => {
  loadingOnChangeQuantity.value = true;
  if (quantity <= 0) {
    removeFromCart(item.cart_id, item.item_id);
    loadingOnChangeQuantity.value = false;
    return;
  }
  cartService.changeQuantity(item.cart_id, item.item_id, quantity).then((response: any) => {
    getCarts();
    loadingOnChangeQuantity.value = false;
  }).catch((error: any) => {
    console.log(error);
  });
};

// Debounce the changeQuantity function
const debouncedChangeQuantity = debounce(changeQuantity, 500);

// Render products
const getProducts = computed(() => {
  return cartStore.items.filter((item: any) => item.clean_product_type === 'Product');
})

// Render Services and Packages
const getServicesAndPackages = computed(() => {
  return cartStore.items.filter((item: any) => item.clean_product_type === 'ClinicService' || item.clean_product_type === 'ClinicPackage');
})

const hasPackages = computed(() => {
  return getServicesAndPackages.value.some((item: any) => item.clean_product_type === 'ClinicPackage');
})

const hasServices = computed(() => {
  return getServicesAndPackages.value.some((item: any) => item.clean_product_type === 'ClinicService');
})

const getOrderId = computed(() => {
  if (!hasServices.value && !hasPackages.value) {
    return getProducts.value.length > 0 ? getProducts.value[0].order_id : null;
  }

  return getServicesAndPackages.value.length > 0 ? getServicesAndPackages.value[0].order_id : null;
});

const getTotalItemsInCart = computed(() => {
  let totalItems = 0;

  cartStore.items.filter((item) => {
    return item.product?.type != 'shipping_method';
  }).forEach((item: any) => {
    totalItems += item.quantity;
  });

  localStorage.setItem('cartItems', String(totalItems));

  return totalItems > 1 ? `${totalItems} items` : `${totalItems} item`;
})

onMounted(() => {
  
  getCarts();
})

return (_ctx: any,_cache: any) => {
  const _component_v_card_title = _resolveComponent("v-card-title")!
  const _component_v_card = _resolveComponent("v-card")!
  const _component_v_alert = _resolveComponent("v-alert")!
  const _component_v_container = _resolveComponent("v-container")!

  return (_openBlock(), _createBlock(_component_v_container, {
    class: "px-0 position-relative",
    style: {"background-color":"#F0F0F0","min-height":"100vh"}
  }, {
    default: _withCtx(() => [
      (getServicesAndPackages.value.length > 0)
        ? (_openBlock(), _createBlock(_component_v_card, {
            key: 0,
            class: "card-container"
          }, {
            default: _withCtx(() => [
              _createVNode(_component_v_card_title, { class: "pt-0 pl-0" }, {
                default: _withCtx(() => [
                  _createElementVNode("p", _hoisted_1, _toDisplayString(hasPackages.value && hasServices.value ? 'Services & Packages' : hasServices.value ? 'Services' : 'Packages'), 1),
                  (getOrderId.value)
                    ? (_openBlock(), _createElementBlock("p", _hoisted_2, [
                        _cache[1] || (_cache[1] = _createElementVNode("span", { class: "font-grey-dark" }, "Order ID:", -1)),
                        _createTextVNode(" #" + _toDisplayString(getOrderId.value), 1)
                      ]))
                    : _createCommentVNode("", true)
                ]),
                _: 1
              }),
              (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(getServicesAndPackages.value, (service, index) => {
                return (_openBlock(), _createBlock(ShopCartServicePackageDetails, {
                  key: `service-${index}`,
                  disabled: loadingOnRemove.value,
                  "is-last-item": index === getServicesAndPackages.value.length - 1,
                  item: service,
                  "justify-end": service.quantity > 1,
                  onDelete: ($event: any) => (removeFromCart(service.cart_id, service.item_id))
                }, {
                  default: _withCtx(() => [
                    (service.quantity > 1)
                      ? (_openBlock(), _createBlock(QuantityEditor, {
                          key: 0,
                          modelValue: service.quantity,
                          "onUpdate:modelValue": [($event: any) => ((service.quantity) = $event), ($event: any) => (_unref(debouncedChangeQuantity)(service, service.quantity))],
                          disable: loadingOnChangeQuantity.value
                        }, null, 8, ["modelValue", "onUpdate:modelValue", "disable"]))
                      : _createCommentVNode("", true)
                  ]),
                  _: 2
                }, 1032, ["disabled", "is-last-item", "item", "justify-end", "onDelete"]))
              }), 128))
            ]),
            _: 1
          }))
        : _createCommentVNode("", true),
      (getProducts.value.length > 0)
        ? (_openBlock(), _createBlock(_component_v_card, {
            key: 1,
            class: "card-container"
          }, {
            default: _withCtx(() => [
              _createVNode(_component_v_card_title, { class: "pt-0 pl-0 mb-2" }, {
                default: _withCtx(() => [
                  _cache[3] || (_cache[3] = _createElementVNode("p", { class: "font-size-18-600" }, " Products ", -1)),
                  (getOrderId.value && !hasPackages.value && !hasServices.value)
                    ? (_openBlock(), _createElementBlock("p", _hoisted_3, [
                        _cache[2] || (_cache[2] = _createElementVNode("span", { class: "font-grey-dark" }, "Order ID:", -1)),
                        _createTextVNode(" #" + _toDisplayString(getOrderId.value), 1)
                      ]))
                    : _createCommentVNode("", true)
                ]),
                _: 1
              }),
              (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(getProducts.value, (product) => {
                return (_openBlock(), _createBlock(ShopCartProductDetails, {
                  key: product.id,
                  disabled: loadingOnRemove.value,
                  item: product,
                  class: "pb-5",
                  onDelete: ($event: any) => (removeFromCart(product.cart_id, product.item_id))
                }, {
                  default: _withCtx(() => [
                    _createVNode(QuantityEditor, {
                      modelValue: product.quantity,
                      "onUpdate:modelValue": [($event: any) => ((product.quantity) = $event), ($event: any) => (_unref(debouncedChangeQuantity)(product, product.quantity))],
                      disable: loadingOnChangeQuantity.value
                    }, null, 8, ["modelValue", "onUpdate:modelValue", "disable"])
                  ]),
                  _: 2
                }, 1032, ["disabled", "item", "onDelete"]))
              }), 128))
            ]),
            _: 1
          }))
        : _createCommentVNode("", true),
      _createElementVNode("section", _hoisted_4, [
        _createVNode(ButtonFixed, {
          disabled: _unref(cartStore).items.length === 0,
          text: `Proceed to Order Summary (${getTotalItemsInCart.value})`,
          "custom-class": "text-white primary-button",
          onClick: _cache[0] || (_cache[0] = ($event: any) => (_unref(router).push({ name: 'order-create' })))
        }, null, 8, ["disabled", "text"])
      ]),
      (_unref(cartStore).items.length == 0)
        ? (_openBlock(), _createBlock(_component_v_card, {
            key: 2,
            class: "mt-4 mx-4 mb-3"
          }, {
            default: _withCtx(() => [
              (!_unref(settingsStore).loading)
                ? (_openBlock(), _createBlock(_component_v_alert, {
                    key: 0,
                    type: "info",
                    variant: "tonal"
                  }, {
                    default: _withCtx(() => [...(_cache[4] || (_cache[4] = [
                      _createTextVNode("No items in cart", -1)
                    ]))]),
                    _: 1
                  }))
                : _createCommentVNode("", true)
            ]),
            _: 1
          }))
        : _createCommentVNode("", true)
    ]),
    _: 1
  }))
}
}

})